import api from './api';

export const courseService = {
    /**
     * Get all modules for a specific level
     * @param {string} levelId - The level ID (e.g., 'ppl', 'pstar')
     * @returns {Promise<Array>} List of modules
     */
    async getModules(levelId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'modules',
                    level_id: levelId
                }
            });
            return response.data || [];
        } catch (error) {
            console.error('API Error (Course Modules):', error);
            throw error;
        }
    },

    /**
     * Get all courses for a specific module
     * @param {number} moduleId - The module ID
     * @returns {Promise<Array>} List of courses
     */
    async getCourses(moduleId) {
        try {
            const response = await api.get('/courses.php', {
                params: { action: 'courses', module_id: moduleId }
            });
            const data = response.data;
            if (Array.isArray(data)) {
                return { courses: data, hasFullCourseAccess: false };
            }
            return {
                courses: data?.courses || [],
                hasFullCourseAccess: !!data?.hasFullCourseAccess,
            };
        } catch (error) {
            console.error('API Error (Courses):', error);
            throw error;
        }
    },

    /**
     * Get course details with chapters
     * @param {number} courseId - The course ID
     * @returns {Promise<Object>} Course details and chapters
     */
    async getCourseDetail(courseId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'detail',
                    course_id: courseId
                }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Course Detail):', error);
            throw error;
        }
    },

    /**
     * Get paragraphs for a specific chapter
     * @param {number} chapterId - The chapter ID
     * @returns {Promise<Array>} List of paragraphs
     */
    async getParagraphs(chapterId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'paragraphs',
                    chapter_id: chapterId
                }
            });
            // Backend returns {paragraphs: [...], chapterAudio: {...}|null}
            const data = response.data;
            if (Array.isArray(data)) {
                // Legacy fallback: plain array
                return { paragraphs: data, chapterAudio: null };
            }
            return { paragraphs: data.paragraphs || [], chapterAudio: data.chapterAudio || null };
        } catch (error) {
            console.error('API Error (Paragraphs):', error);
            throw error;
        }
    },

    /**
     * Get quiz questions and explanations for a chapter
     * @param {number} chapterId - The chapter ID
     * @returns {Promise<Object>} Quiz responses and explanations
     */
    async getQuiz(chapterId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'quiz',
                    chapter_id: chapterId
                }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz):', error);
            throw error;
        }
    },

    /**
     * Start or resume a course session
     * @param {number} courseId - The course ID
     * @returns {Promise<Object>} Session details
     */
    async startSession(courseId) {
        try {
            const response = await api.post('/courses.php?action=start_session', {
                courseId
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Start Session):', error);
            throw error;
        }
    },

    /**
     * Update session duration
     * @param {number} sessionId - The session ID
     * @param {number} duration - Duration in seconds
     * @returns {Promise<Object>} Success status
     */
    async updateSession(sessionId, duration) {
        try {
            const response = await api.post('/courses.php?action=update_session', {
                sessionId,
                duration
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Update Session):', error);
            throw error;
        }
    },

    /**
     * Submit quiz answers
     * @param {number} sessionId - The session ID
     * @param {number} chapterId - The chapter ID
     * @param {Object} answers - User answers {responseId: boolean}
     * @returns {Promise<Object>} Quiz results (passed, score, totalQuestions)
     */
    async submitQuiz(sessionId, chapterId, answers) {
        try {
            const response = await api.post('/courses.php?action=submit_quiz', {
                sessionId,
                chapterId,
                answers
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Submit Quiz):', error);
            throw error;
        }
    },

    /**
     * Get session details
     * @param {number} sessionId - The session ID
     * @returns {Promise<Object>} Session details
     */
    async getSession(sessionId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'session',
                    session_id: sessionId
                }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Get Session):', error);
            throw error;
        }
    },

    /**
     * Validate a section as read
     * @param {number} sessionId - The session ID
     * @param {number} chapterId - The chapter/section ID
     * @returns {Promise<Object>} Updated validated sections
     */
    async validateSection(sessionId, chapterId) {
        try {
            const response = await api.post('/courses.php?action=validate_section', {
                sessionId,
                chapterId
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Validate Section):', error);
            throw error;
        }
    },

    /**
     * Get user progress for a course
     * @param {number} courseId - The course ID
     * @returns {Promise<Object>} Progress data
     */
    async getProgress(courseId) {
        try {
            const response = await api.get('/courses.php', {
                params: {
                    action: 'get_progress',
                    course_id: courseId
                }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Get Progress):', error);
            throw error;
        }
    },

    /**
     * Save quiz progress (for resuming later)
     * @param {number} sessionId - The session ID
     * @param {number} chapterId - The chapter ID
     * @param {Object} answers - User answers {responseId: boolean}
     * @returns {Promise<Object>} Success status
     */
    async saveQuizProgress(sessionId, chapterId, answers) {
        try {
            const response = await api.post('/courses.php?action=save_quiz_progress', {
                sessionId,
                chapterId,
                answers
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Save Quiz Progress):', error);
            throw error;
        }
    },

    /**
     * Mark course as completed and save to history
     * @param {number} courseId - The course ID
     * @param {number} moduleId - The module ID
     * @param {number} duration - Total duration in seconds
     * @param {Object} validatedSections - Sections completed {chapterId: "1"}
     * @param {Object} quizScores - Quiz scores {chapterId: {score: 85, passed: true}}
     * @returns {Promise<Object>} History ID
     */
    async completeCourse(courseId, moduleId, duration, validatedSections, quizScores) {
        try {
            const response = await api.post('/courses.php?action=complete_course', {
                courseId,
                moduleId,
                duration,
                validatedSections,
                quizScores
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Complete Course):', error);
            throw error;
        }
    },

    /**
     * Restart a course session: archives current progress to history and creates a new empty session
     * @param {number} courseId - The course ID
     * @param {number} duration - Duration in seconds of the session being archived
     * @param {Object} validatedSections - Sections completed in current session {chapterId: "1"}
     * @returns {Promise<Object>} New session details {sessionId, startTime, duration}
     */
    async restartSession(courseId, duration, validatedSections) {
        try {
            const response = await api.post('/courses.php?action=restart_session', {
                courseId,
                duration,
                validatedSections
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Restart Session):', error);
            throw error;
        }
    },

    /**
     * Get user course history
     * @param {number} moduleId - Optional module ID to filter
     * @returns {Promise<Array>} Course history
     */
    // ===== Quiz (Exam-style) Methods =====

    async getQuizQuestions(courseId) {
        try {
            const response = await api.get('/courses.php', {
                params: { action: 'quiz_questions', course_id: courseId }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz Questions):', error);
            throw error;
        }
    },

    async getQuizAnswers(courseId) {
        try {
            const response = await api.get('/courses.php', {
                params: { action: 'quiz_answers', course_id: courseId }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz Answers):', error);
            throw error;
        }
    },

    async getQuizSession(courseId) {
        try {
            const response = await api.get('/courses.php', {
                params: { action: 'quiz_get_session', course_id: courseId }
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz Get Session):', error);
            throw error;
        }
    },

    async saveQuizSession(courseId, data) {
        try {
            const response = await api.post('/courses.php?action=quiz_save_session', {
                courseId,
                ...data
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz Save Session):', error);
            throw error;
        }
    },

    async saveQuizHistory(sessionId, data) {
        try {
            const response = await api.post('/courses.php?action=quiz_save_history', {
                sessionId,
                ...data
            });
            return response.data;
        } catch (error) {
            console.error('API Error (Quiz Save History):', error);
            throw error;
        }
    },

    async getUserHistory(moduleId = null) {
        try {
            const params = { action: 'get_user_history' };
            if (moduleId) params.module_id = moduleId;

            const response = await api.get('/courses.php', { params });
            return response.data || [];
        } catch (error) {
            console.error('API Error (Get User History):', error);
            throw error;
        }
    },

    async getGroupPdfs(moduleId) {
        try {
            const response = await api.get('/courses.php', {
                params: { action: 'group_pdfs', module_id: moduleId }
            });
            return response.data || [];
        } catch (error) {
            console.error('API Error (Group PDFs):', error);
            return [];
        }
    },

    getGroupPdfUrl(moduleId, groupName, lang = 'fr') {
        const baseUrl = api.defaults.baseURL || '/backend/api/v1';
        return `${baseUrl}/courses.php?action=download_group_pdf&module_id=${encodeURIComponent(moduleId)}&group_name=${encodeURIComponent(groupName)}&lang=${encodeURIComponent(lang)}`;
    }
};

export default courseService;
